import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Mail } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './DnaHelix.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BYPASS_PATHS = ['/login', '/register', '/admin'];

const LockScreen = ({ children }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [locked, setLocked] = useState(null);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const isBypassed = BYPASS_PATHS.some(p => location.pathname.startsWith(p));
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const checkLock = async () => {
      try {
        const { data } = await axios.get(`${API}/site-status`);
        setLocked(data.locked);
      } catch {
        setLocked(false);
      }
    };
    checkLock();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/early-access`, { email });
      setSubmitted(true);
    } catch {
      // still show success to avoid leaking info
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  // Still loading status
  if (locked === null) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Site is unlocked, path is bypassed, or user is admin — render the real app
  if (!locked || isBypassed || isAdmin) return children;

  // Site is locked — show lock screen
  return (
    <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center relative overflow-hidden" data-testid="lock-screen">
      {/* DNA helix background */}
      <div className="dna-container" aria-hidden="true">
        <div className="dna-scene" style={{ right: '80px', opacity: 0.3 }}>
          <div className="dna-strand">
            {Array.from({ length: 20 }).map((_, i) => (
              <div key={i} className="dna-rung" style={{ '--i': i }}>
                <span className="dna-node dna-node-left" />
                <span className="dna-bridge" />
                <span className="dna-node dna-node-right" />
              </div>
            ))}
          </div>
        </div>
        <div className="dna-scene dna-scene-secondary" style={{ left: '50px', opacity: 0.15 }}>
          <div className="dna-strand dna-strand-reverse">
            {Array.from({ length: 16 }).map((_, i) => (
              <div key={i} className="dna-rung" style={{ '--i': i }}>
                <span className="dna-node dna-node-left" />
                <span className="dna-bridge" />
                <span className="dna-node dna-node-right" />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative z-10 text-center px-6 max-w-lg w-full">
        {/* Logo */}
        <h1
          className="text-4xl sm:text-5xl font-bold text-white mb-3 tracking-tight"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          RENEW<span className="text-[#00F0FF]">|</span>LABS
        </h1>
        <p className="text-[#A1A7B5] text-sm mb-12 tracking-[0.2em] uppercase">
          Research Peptides
        </p>

        {!submitted ? (
          <>
            <h2
              className="text-2xl sm:text-3xl font-bold text-white mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Coming Soon
            </h2>
            <p className="text-[#A1A7B5] mb-8">
              We're preparing something special. Enter your email to get early access.
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <div className="relative flex-1">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="w-full bg-[#111621] border border-[#1E2532] text-white rounded-lg px-4 py-3 pl-10 text-sm placeholder-[#A1A7B5] focus:outline-none focus:border-[#00F0FF] transition-colors"
                  data-testid="early-access-email"
                />
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A7B5]" />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="bg-[#00F0FF] hover:bg-[#00d4e0] text-black font-semibold rounded-lg px-6 py-3 text-sm transition-colors disabled:opacity-50 whitespace-nowrap"
                data-testid="early-access-submit"
              >
                {loading ? 'Submitting...' : 'Get Early Access'}
              </button>
            </form>
          </>
        ) : (
          <>
            <div className="w-16 h-16 mx-auto mb-6 bg-[#00F0FF]/10 rounded-full flex items-center justify-center">
              <Mail className="w-8 h-8 text-[#00F0FF]" />
            </div>
            <h2
              className="text-2xl font-bold text-white mb-3"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              You're on the list!
            </h2>
            <p className="text-[#A1A7B5]">
              We'll notify you at <span className="text-white">{email}</span> when we launch.
            </p>
          </>
        )}

        {/* Instagram link */}
        <div className="mt-16">
          <a
            href="https://www.instagram.com/renew.labs"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 text-[#A1A7B5] hover:text-[#00F0FF] transition-colors text-sm"
            data-testid="lock-screen-instagram"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C16.67.014 16.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
            <span>@renew.labs</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default LockScreen;
